'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import ServiceCard from '@/app/homepage/components/ServiceCard';
import ProjectCard from '@/app/homepage/components/ProjectCard';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Navigation from '@/components/common/Navigation';
import { rfpService, RfpFormData } from '@/lib/services/rfpService';

export default function IndustrialServicesPage() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<RfpFormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: 'industrial',
    message: '',
    servicePage: 'industrial',
  });

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));
    
    return () => observer?.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    const result = await rfpService.submitInquiry(formData);
    if (result.success) {
      setSubmitStatus('success');
      setFormData({ name: '', company: '', email: '', phone: '', projectType: 'industrial', message: '', servicePage: 'industrial' });
    } else {
      setSubmitStatus('error');
      setErrorMessage(result.error || 'Failed to submit inquiry.');
    }
    setIsSubmitting(false);
  };

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center border-b border-border">
        <div className="absolute inset-0">
          <img
            src="/assets/images/underground-drilling-header_media_library_original_2160_945-1775175753941.jpeg"
            alt="Aerial view of a large directional drilling operation near a body of water"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <Link 
            href="/homepage" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 reveal"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back to Home
          </Link>
          
          <div className="mb-6 reveal" style={{ transitionDelay: '100ms' }}>
            <Icon name="/assets/images/dgfjghkhjljkoi-1771377505784.png" size={64} className="text-warning mx-auto mb-6" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground mb-6 reveal" style={{ transitionDelay: '200ms' }}>
            Industrial Projects
            <br />
            <span className="text-gradient-orange">Infrastructure</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 reveal" style={{ transitionDelay: '300ms' }}>
            Specialized horizontal directional drilling for chemical plants, refineries, and manufacturing facilities. 
            Expert handling of hazardous materials and complex industrial environments.
          </p>
          
          <button
            onClick={() => setShowForm(true)}
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all reveal"
            style={{ transitionDelay: '400ms' }}
          >
            Request Industrial Project Quote
          </button>
        </div>
      </section>

      {/* Equipment Specifications */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Equipment & Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Industrial-Grade Equipment
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Heavy-duty drilling systems designed for demanding industrial environments with enhanced safety features.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard 
              icon="CogIcon"
              title="Vermeer D330x500 Heavy-Duty Rig"
              description="Maximum-capacity drilling system for large-diameter industrial piping and process lines."
              specs={[
                'Thrust/Pullback: 330,000 lbs',
                'Torque: 50,000 ft-lbs',
                'Pipe diameter: 24\" to 48\"',
                'Max bore length: 2,500 feet',
                'Ideal for: Chemical plants, refineries'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="CogIcon"
              title="Vermeer D60x90 Industrial Rig"
              description="Versatile drilling platform for medium-diameter industrial utility installations."
              specs={[
                'Thrust/Pullback: 60,000 lbs',
                'Torque: 9,000 ft-lbs',
                'Pipe diameter: 6\" to 24\"',
                'Max bore length: 1,500 feet',
                'Ideal for: Process piping, cooling systems'
              ]}
              delay={200}
            />
            
            <ServiceCard 
              icon="ShieldCheckIcon"
              title="Hazmat Safety Systems"
              description="Specialized safety equipment and protocols for working in hazardous industrial environments."
              specs={[
                'Continuous gas monitoring systems',
                'Explosion-proof equipment',
                'Hazmat-certified operators',
                'Emergency response protocols',
                'Confined space entry systems'
              ]}
              delay={300}
            />
            
            <ServiceCard 
              icon="/assets/images/dgfjghkhjljkoi-1771377505784.png"
              title="Industrial Pipe Installation"
              description="Specialized equipment for installing industrial process piping with precision and safety."
              specs={[
                'Carbon steel pipe installation',
                'Stainless steel piping systems',
                'HDPE chemical-resistant pipe',
                'High-pressure pipe capabilities',
                'Corrosion-resistant materials'
              ]}
              delay={400}
            />
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <div className="hidden">
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Project Portfolio
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Industrial Infrastructure Case Studies
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Proven expertise in complex industrial projects requiring specialized equipment and safety protocols.
            </p>
          </div>
          
          <div className="space-y-0">
            <ProjectCard 
              title="Chemical Plant Process Piping"
              location="Wilmington, DE"
              challenge="Install 1,200 feet of 24-inch chemical process piping beneath active chemical plant without disrupting production. Required working in hazardous environment with strict safety protocols and coordination with plant operations."
              solution="Deployed Vermeer D330x500 with explosion-proof equipment and continuous gas monitoring. Hazmat-certified crew worked in 4-hour shifts with full PPE. Installed corrosion-resistant HDPE pipe with fusion welding. Zero safety incidents and no production downtime."
              timeline="Completed in 35 days"
              image="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=800"
              imageAlt="Chemical plant process piping installation with hazmat safety equipment in Wilmington"
              delay={100}
            />
            
            <ProjectCard 
              title="Refinery Cooling Water System"
              location="Marcus Hook, PA"
              challenge="Install 2,000 feet of 36-inch cooling water intake line from Delaware River to refinery cooling towers. Required drilling beneath active railroad, tank farm, and process units during refinery turnaround with tight deadline."
              solution="Executed precision HDD with real-time navigation to avoid underground utilities and foundations. Installed carbon steel pipe with cathodic protection. Coordinated with refinery operations for hot work permits. Completed ahead of turnaround schedule."
              timeline="Completed in 28 days"
              image="https://images.unsplash.com/photo-1565689157206-0fddef7589a2?w=800"
              imageAlt="Refinery cooling water system installation near Delaware River in Marcus Hook"
              delay={200}
            />
            
            <ProjectCard 
              title="Manufacturing Facility Utilities"
              location="Elkton, MD"
              challenge="Install complete underground utility infrastructure for new 500,000 sq ft manufacturing facility including water, sewer, natural gas, electrical, and compressed air systems. Required coordination of multiple utility installations with tight construction schedule."
              solution="Deployed multiple drilling rigs for parallel installation of utility corridors. Installed 15 separate HDD bores ranging from 300 to 1,500 feet. Coordinated with general contractor for building foundation work. Completed all utilities before building occupancy deadline."
              timeline="Completed in 60 days"
              image="https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=800"
              imageAlt="Manufacturing facility utility infrastructure installation with multiple drilling operations"
              delay={300}
            />
            
            <ProjectCard 
              title="Power Plant Steam Line Installation"
              location="Carneys Point, NJ"
              challenge="Install 800 feet of 30-inch high-pressure steam line from boiler house to turbine building beneath active power plant. Required working around high-voltage equipment and maintaining plant operations during installation."
              solution="Precision HDD with electromagnetic locating to avoid existing utilities. Installed insulated carbon steel pipe with expansion joints. Hot work permits and electrical safety protocols for working near energized equipment. Zero electrical outages during installation."
              timeline="Completed in 22 days"
              image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800"
              imageAlt="Power plant steam line installation with high-voltage safety protocols in New Jersey"
              delay={400}
            />
          </div>
        </div>
      </section>
      </div>

      {/* Technical Expertise */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Technical Expertise
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Industrial Specializations
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '100ms' }}>
              <Icon name="BeakerIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Chemical Plants</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Process piping systems
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Chemical transfer lines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Hazardous material handling
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Tank farm connections
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '200ms' }}>
              <Icon name="FireIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Refineries</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Crude oil pipelines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Product transfer lines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Cooling water systems
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Utility infrastructure
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '300ms' }}>
              <Icon name="CogIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Manufacturing</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Facility utility systems
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Compressed air lines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Process water systems
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Waste handling infrastructure
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 reveal">
            Ready to Start Your Industrial Project?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 reveal" style={{ transitionDelay: '100ms' }}>
            Contact our industrial specialists for a detailed project assessment and competitive quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center reveal" style={{ transitionDelay: '200ms' }}>
            <Link
              href="/contact"
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all"
            >
              Get Project Quote
            </Link>
            <Link href="/homepage">
              <button className="px-8 py-4 border-2 border-foreground text-foreground rounded-lg font-bold text-lg hover:bg-foreground hover:text-background transition-all">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* RFP Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button
              onClick={() => { setShowForm(false); setSubmitStatus('idle'); }}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="XMarkIcon" size={24} className="text-foreground" />
            </button>
            <h2 className="text-2xl font-bold text-foreground mb-2">Request Industrial Project Quote</h2>
            <p className="text-muted-foreground mb-6">Fill out the form below and we will respond within 48 hours.</p>
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="CheckCircleIcon" size={40} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Inquiry Submitted!</h3>
                <p className="text-muted-foreground mb-4">Thank you! We will contact you within 48 hours.</p>
                <button onClick={() => { setShowForm(false); setSubmitStatus('idle'); }} className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all">Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="John Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Company *</label>
                    <input type="text" name="company" required value={formData.company} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Acme Corp" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="john@company.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone *</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Project Details *</label>
                  <textarea name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Tell us about your industrial project requirements, timeline, and location..." />
                </div>
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <Icon name="ExclamationCircleIcon" size={18} className="text-destructive flex-shrink-0" />
                    <p className="text-sm text-destructive">{errorMessage}</p>
                  </div>
                )}
                <button type="submit" disabled={isSubmitting} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSubmitting ? (
                    <><svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Submitting...</>
                  ) : 'Submit RFP Inquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}